import { useState, useRef, useEffect } from 'react';
import type React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import AiChat from './components/chat/ai-chat';
import SessionsSidebar from './components/sessions-sidebar/sessions-sidebar';

import { useCurrentUser } from 'state/current-user/current-user.thunk';
import { useImpersonation } from 'hooks/use-impersonation';

import { ROLES } from 'constants/roles';
import { AiAssistantSessionProvider } from './context/ai-assistant-session-context';

const AiAssistantPage = (): React.JSX.Element => {
  const { data: currentUser } = useCurrentUser();
  const { isImpersonating } = useImpersonation();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const focusMessageInputRef = useRef<(() => void) | null>(null);

  const clientId = (params.clientId ||
    (currentUser as unknown as { client?: { id: string | number } })?.client
      ?.id) as string | number;
  const isReadOnly =
    isImpersonating() &&
    (currentUser as unknown as { authority?: string })?.authority ===
      ROLES.USER;
  const conversationId = searchParams.get('conversationId');

  useEffect(() => {
    focusMessageInputRef.current?.();
  }, []);

  const handleToggleSidebar = (): void => {
    setIsSidebarOpen((prev) => !prev);
  };

  return (
    <AiAssistantSessionProvider>
      <div className="flex flex-col flex-1 w-full p-4 bg-navy-50 mb-[-24px]">
        <div className="relative flex flex-row flex-1 border rounded-lg shadow-xs bg-white">
          <SessionsSidebar
            isOpen={isSidebarOpen}
            onToggle={handleToggleSidebar}
            conversationId={conversationId}
            setSearchParams={setSearchParams as never}
            clientId={clientId}
            onFocusMessageInput={() => focusMessageInputRef.current?.()}
            isReadOnly={isReadOnly}
          />
          <AiChat
            isSidebarOpen={isSidebarOpen}
            conversationId={conversationId}
            setSearchParams={setSearchParams as never}
            clientId={clientId}
            registerFocusMessageInput={(fn) => {
              focusMessageInputRef.current = fn;
            }}
            isReadOnly={isReadOnly}
          />
        </div>
      </div>
    </AiAssistantSessionProvider>
  );
};

export default AiAssistantPage;
